import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerType, Question, Option } from '@amzn/red-velvet-api';

const OPTION_LABEL_MAX_LENGTH = 100;
const MAX_QUESTIONS = 3;
const MAX_OPTIONS = 5;
const MIN_CHOICE_OPTIONS = 2;

/** UI wrapper that pairs a Question with a Select-compatible answerType option. */
export interface QuestionFormState {
  question: Question;
  answerTypeOption: { label: string; value: AnswerType } | null;
}

export const isChoiceType = (answerType?: AnswerType): boolean =>
  answerType === AnswerType.CHOICES_MULTIPLE || answerType === AnswerType.CHOICES_SINGLE;

const generateId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function useCustomQuestions(
  initialQuestions?: Question[],
  onChanged?: (questions: Question[]) => void
) {
  const { t } = useTranslation(['events', 'translation']);

  const answerTypeOptions: { label: string; value: AnswerType }[] = [
    { label: t('shiftStep.answerTypeTextSingle'), value: AnswerType.TEXT_SINGLE },
    { label: t('shiftStep.answerTypeChoicesMultiple'), value: AnswerType.CHOICES_MULTIPLE },
    { label: t('shiftStep.answerTypeChoicesSingle'), value: AnswerType.CHOICES_SINGLE },
  ];

  const [items, setItems] = useState<QuestionFormState[]>(() =>
    (initialQuestions ?? []).map((q) => ({
      question: { ...q, id: q.id || generateId() },
      answerTypeOption:
        answerTypeOptions.find((opt) => opt.value === q.answerType) ?? answerTypeOptions[0],
    }))
  );
  const [questionErrors, setQuestionErrors] = useState<Record<string, string>>({});
  const [optionErrors, setOptionErrors] = useState<Record<string, string>>({});

  const notify = (newItems: QuestionFormState[]) => {
    onChanged?.(newItems.map((i) => i.question));
  };

  /** Returns the Question[] array ready to send to the API. */
  const toApiQuestions = (): Question[] => items.map((item) => item.question);

  const addQuestion = () => {
    if (items.length >= MAX_QUESTIONS) return;
    const newItems = [
      ...items,
      {
        question: { id: generateId(), title: '', answerType: AnswerType.TEXT_SINGLE, options: [] },
        answerTypeOption: answerTypeOptions[0],
      },
    ];
    setItems(newItems);
    notify(newItems);
  };

  const removeQuestion = (questionId: string) => {
    const newItems = items.filter((item) => item.question.id !== questionId);
    setItems(newItems);
    notify(newItems);
  };

  const updateTitle = (questionId: string, title: string) => {
    setItems(
      items.map((item) =>
        item.question.id === questionId ? { ...item, question: { ...item.question, title } } : item
      )
    );
  };

  const updateAnswerType = (questionId: string, selected: { label: string; value: AnswerType }) => {
    const isChoice = isChoiceType(selected.value);
    const newItems = items.map((item) => {
      if (item.question.id !== questionId) return item;
      const currentOptions = item.question.options ?? [];
      const options =
        isChoice && currentOptions.length < MIN_CHOICE_OPTIONS
          ? [
              { id: generateId(), label: '' },
              { id: generateId(), label: '' },
            ]
          : isChoice
            ? currentOptions
            : [];
      return {
        answerTypeOption: selected,
        question: { ...item.question, answerType: selected.value, options },
      };
    });
    setItems(newItems);
    notify(newItems);
  };

  const addOption = (questionId: string) => {
    const newItems = items.map((item) => {
      if (item.question.id !== questionId) return item;
      const options = item.question.options ?? [];
      if (options.length >= MAX_OPTIONS) return item;
      return {
        ...item,
        question: { ...item.question, options: [...options, { id: generateId(), label: '' }] },
      };
    });
    setItems(newItems);
    clearOptionError(questionId);
    notify(newItems);
  };

  const removeOption = (questionId: string, optionId: string) => {
    const newItems = items.map((item) => {
      if (item.question.id !== questionId) return item;
      const remaining = (item.question.options ?? []).filter((o) => o.id !== optionId);
      if (remaining.length === 0) {
        setOptionErrors((prev) => ({ ...prev, [questionId]: t('shiftStep.optionsRequired') }));
      }
      return { ...item, question: { ...item.question, options: remaining } };
    });
    setItems(newItems);
    notify(newItems);
  };

  const updateOptionLabel = (questionId: string, optionId: string, label: string) => {
    if (label.length > OPTION_LABEL_MAX_LENGTH) return;
    setItems(
      items.map((item) => {
        if (item.question.id !== questionId) return item;
        return {
          ...item,
          question: {
            ...item.question,
            options: (item.question.options ?? []).map((o: Option) =>
              o.id === optionId ? { ...o, label } : o
            ),
          },
        };
      })
    );
  };

  const clearQuestionError = (questionId: string) => {
    setQuestionErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const setQuestionError = (questionId: string, message: string) => {
    setQuestionErrors((prev) => ({ ...prev, [questionId]: message }));
  };

  const clearOptionError = (questionId: string) => {
    setOptionErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const setOptionError = (questionId: string, message: string) => {
    setOptionErrors((prev) => ({ ...prev, [questionId]: message }));
  };

  return {
    items,
    questionErrors,
    optionErrors,
    answerTypeOptions,
    canAddQuestion: items.length < MAX_QUESTIONS,
    maxOptions: MAX_OPTIONS,
    toApiQuestions,
    addQuestion,
    removeQuestion,
    updateTitle,
    updateAnswerType,
    addOption,
    removeOption,
    updateOptionLabel,
    clearQuestionError,
    setQuestionError,
    clearOptionError,
    setOptionError,
  };
}
